import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Plus, PlusCircle, Layers, Trash2, BookOpen, User, Clock, Pencil, Database, Archive, Loader2 } from 'lucide-react';
import AddKnowledgeBaseModal from './AddKnowledgeBaseModal';
import KnowledgebaseManagementModal from './KnowledgebaseManagementModal';
import KnowledgeEntityModal from './KnowledgeEntityModal';
import { ingestionService } from '../../../../components/api/ingestionService';
import { getUserInfo } from '../../../../routes/decodedAdmin';
import ConfirmDialog from '../../Dialogs/ConfirmDialog';
import RenameModal from '../../common/RenameModal';
import { knowledgebaseService } from '../../../../components/api/knowledgebaseService';
interface Entity {
    id: string;
    name: string;
}

interface KnowledgeWorkspace {
    id: string;
    tenantId: string;
    title: string;
    description: string;
    tags: string[];
    createdBy: string;
    createdOn: string;
    vectorSore?: string;
    vaultId?: string;
    destinationType?: string;
    destination?: string | null;
}

const DataSourceListingPage: React.FC = () => {
    const navigate = useNavigate();
    const { workspaceId: routeWorkspaceId, kbId: routeKbId } = useParams<{ workspaceId?: string; kbId?: string }>();
    const [searchParams] = useSearchParams();
    const deepLinkWorkspaceId = useMemo(() => {
        const fromRoute = routeWorkspaceId?.trim();
        if (fromRoute) return fromRoute;
        return (
            searchParams.get('workspaceId')?.trim()
            || searchParams.get('knowledgeBaseId')?.trim()
            || null
        );
    }, [routeWorkspaceId, searchParams]);
    const deepLinkKbId = useMemo(() => {
        const fromRoute = routeKbId?.trim();
        if (fromRoute) return fromRoute;
        return searchParams.get('kbId')?.trim() || null;
    }, [routeKbId, searchParams]);
    const openedDeepLinkRef = useRef<string | null>(null);

    const [search, setSearch] = useState('');
    const [workspaces, setWorkspaces] = useState<KnowledgeWorkspace[]>([]);
    const [loading, setLoading] = useState(true);
    const [showEntityModal, setShowEntityModal] = useState(false);
    const [selectedWorkspace, setSelectedWorkspace] = useState<KnowledgeWorkspace | null>(null);
    const [showKBModal, setShowKBModal] = useState(false);
    const [workspaceToDelete, setWorkspaceToDelete] = useState<KnowledgeWorkspace | null>(null);

    useEffect(() => {
        openedDeepLinkRef.current = null;
    }, [deepLinkWorkspaceId]);

    useEffect(() => {
        fetchWorkspaces();
    }, []);
    const fetchWorkspaces = async () => {
        setLoading(true);
        try {
            const response = await ingestionService.loadkbEntitiesList();
            setWorkspaces(response);
        } catch (err) {
            console.error('Failed to fetch workspaces', err);
        } finally {
            setLoading(false);
        }
    };
    const filtered = workspaces.filter(w =>
        w.title.toLowerCase().includes(search.toLowerCase())
    );

    const handleDelete = async () => {
        const token = localStorage.getItem('token'); // or whatever key you use
        if (token) {
            const userInfo = getUserInfo(token);
            try {
                if (userInfo) {
                    await ingestionService.deleteKBEntityGroup(workspaceToDelete?.id, userInfo.sub);
                    setWorkspaceToDelete(null);
                    fetchWorkspaces();
                }
            } catch (err) {
                console.error('Failed to delete workspace', err);
                alert('Failed to delete workspace.');
            }
        }
        if (!workspaceToDelete) return;

    };


    const handleManage = (workspace: KnowledgeWorkspace) => {
        setSelectedWorkspace(workspace);
        setShowKBModal(true);
    };

    const handleCloseKBModal = () => {
        setShowKBModal(false);
        setSelectedWorkspace(null);
        openedDeepLinkRef.current = null;
        if (deepLinkWorkspaceId || deepLinkKbId) {
            navigate('/portal/knowledge-base', { replace: true });
        }
    };

    useEffect(() => {
        if (!deepLinkWorkspaceId || loading) return;
        if (openedDeepLinkRef.current === deepLinkWorkspaceId) return;

        const workspace = workspaces.find((w) => w.id === deepLinkWorkspaceId);
        if (workspace) {
            openedDeepLinkRef.current = deepLinkWorkspaceId;
            setSelectedWorkspace(workspace);
            setShowKBModal(true);
        }
    }, [deepLinkWorkspaceId, workspaces, loading]);

    const [showRenameModal, setShowRenameModal] = useState(false);
    const [renameTarget, setRenameTarget] = useState<{ id: string; name: string } | null>(null);


    return (
        <div className="px-6 py-8">
            {/* Header */}
            <div className="flex justify-between items-center mb-6">
                <div>
                    <h2 className="text-xl font-semibold text-white">Available Knowledge Workspaces</h2>
                    <p className="text-sm text-gray-400">Group, manage, and configure your entities here.</p>
                </div>
                <button
                    onClick={() => setShowEntityModal(true)}
                    className="flex items-center gap-1 bg-indigo-600 hover:bg-indigo-500 px-3 py-1.5 rounded text-[11px] font-medium shadow text-white"
                >
                    <Plus className="w-4 h-4" /> Add New
                </button>
            </div>

            {/* Search */}
            <div className="mb-6">
                <input
                    type="text"
                    placeholder="Search workspaces..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="w-full px-4 py-2 rounded-md bg-neutral-800 text-white placeholder-gray-400 focus:outline-none text-sm"
                />
            </div>

            {/* Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                {loading ? (
                    <div className="col-span-full flex flex-col items-center justify-center py-16 text-gray-400" aria-busy="true">
                        <Loader2 className="w-8 h-8 animate-spin text-indigo-400 mb-3" />
                        <span className="text-sm">Loading workspaces…</span>
                    </div>
                ) : filtered.map((workspace) => (
                    <div
                        key={workspace.id}
                        className="bg-neutral-900 border border-neutral-800 rounded-lg p-5 hover:border-indigo-500/50 transition-all text-sm flex flex-col"
                    >
                        <div className="flex items-start justify-between gap-2 mb-3">
                            <div className="flex items-center gap-2 min-w-0">
                                <BookOpen className="w-5 h-5 text-indigo-400 shrink-0" />
                                <h3 className="text-white text-base font-medium truncate" title={workspace.title}>
                                    {workspace.title}
                                </h3>
                            </div>
                        </div>
                        <p className="text-xs text-gray-400 mb-3 line-clamp-2">{workspace.description}</p>

                        <div className="text-gray-400 text-xs space-y-1.5 mb-3">
                            <div className="flex items-center gap-2">
                                <User className="w-3.5 h-3.5 text-gray-500 shrink-0" />
                                <span className="truncate">{workspace.createdBy || "Unknown"}</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <Clock className="w-3.5 h-3.5 text-gray-500 shrink-0" />
                                <span>
                                    {workspace.createdOn && new Date(workspace.createdOn).getFullYear() > 2000
                                        ? new Date(workspace.createdOn).toLocaleString()
                                        : "—"}
                                </span>
                            </div>
                            {workspace.vectorSore && (
                                <div className="flex items-center gap-2" title="Vector store">
                                    <Database className="w-3.5 h-3.5 text-indigo-400 shrink-0" aria-hidden />
                                    <span className="truncate" title={workspace.vectorSore}>
                                        {workspace.vectorSore}
                                    </span>
                                </div>
                            )}
                            {workspace.vaultId && (
                                <div className="flex items-center gap-2" title="Data vault">
                                    <Archive className="w-3.5 h-3.5 text-amber-400 shrink-0" aria-hidden />
                                    <span className="truncate" title={workspace.vaultId}>
                                        {workspace.vaultId}
                                    </span>
                                </div>
                            )}
                        </div>

                        {workspace.tags && workspace.tags.length > 0 && (
                            <div className="flex flex-wrap gap-1.5 mb-4">
                                <Layers className="w-3.5 h-3.5 text-gray-500 shrink-0 mt-0.5" />
                                {workspace.tags.map((tag) => (
                                    <span
                                        key={tag}
                                        className="inline-block bg-neutral-800 text-gray-300 px-2 py-0.5 rounded-full text-[11px] font-medium hover:bg-indigo-800/40 transition cursor-default max-w-[140px] truncate"
                                        title={tag}
                                    >
                                        {tag.length > 24 ? tag.slice(0, 24) + "…" : tag}
                                    </span>
                                ))}
                            </div>
                        )}

                        <div className="flex justify-end gap-2 mt-auto pt-2 border-t border-neutral-800">
                            <button
                                onClick={() => handleManage(workspace)}
                                className="text-indigo-400 hover:text-indigo-300 text-xs flex items-center"
                            >
                                <PlusCircle className="w-4 h-4 mr-1" /> Manage
                            </button>
                            <button
                                onClick={() => {
                                    setRenameTarget({ id: workspace.id, name: workspace.title });
                                    setShowRenameModal(true);
                                }}
                                className="text-yellow-400 hover:text-yellow-300 p-1"
                                title="Edit Source"
                            >
                                <Pencil className="w-4 h-4" />
                            </button>
                            <button
                                className="text-red-400 hover:text-red-300 p-1"
                                onClick={() => setWorkspaceToDelete(workspace)}
                                title="Delete Workspace"
                            >
                                <Trash2 className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                ))}
                {!loading && filtered.length === 0 && (
                    <div className="col-span-full text-center text-gray-500 py-8">No workspaces found.</div>
                )}
            </div>

            {/* Modals */}
            {showEntityModal && (
                <KnowledgeEntityModal
                    tenantId="2f4e558c-15e5-4246-965d-c107088b1ddb"
                    userId="2f4e558c-15e5-4246-965d-c107088b1ddb"
                    onClose={() => setShowEntityModal(false)}
                    onSubmit={(data) => {
                        fetchWorkspaces()
                    }}
                />
            )}

            {showKBModal && selectedWorkspace && (
                <KnowledgebaseManagementModal
                    workspaceid={selectedWorkspace.id}
                    kbId={deepLinkKbId}
                    onClose={handleCloseKBModal}
                />
            )}

            {workspaceToDelete != null && (
                <ConfirmDialog
                    message={`Are you sure you want to delete "${workspaceToDelete.title}"?`}
                    onYes={handleDelete}
                    onCancel={() => setWorkspaceToDelete(null)}
                />
            )}


            {renameTarget && showRenameModal && (
                <RenameModal
                    id={renameTarget.id}
                    currentName={renameTarget.name}
                    module="Knowledge Workspace"
                    onCancel={() => {
                        setShowRenameModal(false);
                        setRenameTarget(null);
                    }}
                    onSave={({ id, newName }) => {
                        knowledgebaseService.renameSource({
                            knowledgeBaseId: id,
                            tenantID: '3fa85f64-5717-4562-b3fc-2c963f66afa6',
                            newTitle: newName,
                        }).then(() => {
                            fetchWorkspaces();
                            setShowRenameModal(false);
                            setRenameTarget(null);
                        });
                    }}

                />
            )}


        </div>
    );
};

export default DataSourceListingPage;
